import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../../../components/Headers/PageHeader";
import { SearchInput } from "../../../../components/New/SearchInput";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  OrderList,
  OrderListFilters,
  OrderListParams,
  StoreFilterOption,
} from "../../../../@types/Orders";
import {
  defaultOrderProcessStatusFilters,
  filterFulfillmentStatusOptions,
  filterStatusOptions,
  isOrderFulfillmentStatus,
  isOrderStatus,
  OrderFulfillmentStatus,
  OrderStatus,
  OrderStatusConverter,
} from "../../../../enums/Orders";
import { wrapperRequests } from "../../../../services/api";
import { routesURL } from "../../../../services/routesUrl";
import * as S from "./styles";
import { ColumnDef } from "@tanstack/react-table";
import { CopyContainer } from "../../../../components/TableElements/components/CopyContainer";
import { ConvertStringWithoutValueToDefault } from "../../../../shared/ConvertStringWithoutValueToDefault";
import { formatDateWithTenantFormat } from "../../../../shared/Formatter";
import { Table } from "../../../../components/New/Table";
import { ReduceString } from "../../../../shared/ReduceString";
import { SelectInput } from "../../../../components/SelectInput";
import { Loading } from "../../../../components/New/Loading";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { NoDataAvailableImage } from "../../../../components/NoDataAvailableImage";
import { DefaultRowsPerPage } from "../../../../components/New/Table/constants";
import { useTranslateStatus } from "./hooks/useTranslateStatus";
import { useAuth } from "../../../../hooks/auth";
import {
  StoreFilterSelect,
  StoreFilterSelectOption,
} from "./components/StoreFilterSelect";

const SHOPS_SEARCH_PARAM = "shops";

interface ShopFilterParam {
  tenantName: string;
  shopId: string;
}

const getStoreFilterOptions = (
  stores: StoreFilterOption[],
): StoreFilterSelectOption[] =>
  stores
    .map((store) => {
      const shopId = getStoreShopId(store);

      return {
        id: store.id,
        value: getStoreFilterValue(store),
        label: store.name || `${store.tenantName} - ${shopId}`,
      };
    })
    .filter((store) => store.value);

const getShopFilterParam = (
  storeFilterValue: string,
): ShopFilterParam | undefined => {
  const [tenantName, shopId] = storeFilterValue.split("|");

  if (!tenantName || !shopId) {
    return undefined;
  }

  return { tenantName, shopId };
};

const getShopFilterParams = (storeFilterValues: string[]): ShopFilterParam[] =>
  storeFilterValues
    .map((storeFilterValue) => getShopFilterParam(storeFilterValue))
    .filter((store): store is ShopFilterParam => Boolean(store));

const getShopFilterValuesFromSearchParams = (
  searchParams: URLSearchParams,
): string[] | undefined => {
  const shops = searchParams.get(SHOPS_SEARCH_PARAM);

  if (shops === null) {
    return undefined;
  }

  try {
    const parsedShops = JSON.parse(shops);

    if (Array.isArray(parsedShops)) {
      return parsedShops.filter(
        (shop): shop is string => typeof shop === "string",
      );
    }
  } catch {
    return [];
  }

  return [];
};

const getStoreShopId = (store: StoreFilterOption): string =>
  store.shopId || store.shopID || store.shopCode || store.code || "";

const getStoreFilterValue = (store: StoreFilterOption): string => {
  const shopId = getStoreShopId(store);

  return store.tenantName && shopId ? `${store.tenantName}|${shopId}` : "";
};

const getSearchParamFilterValue = <T extends string>(
  searchParams: URLSearchParams,
  key: string,
  validator: (value: string) => value is T,
): T | undefined => {
  const value = searchParams.get(key);

  if (!value) {
    return undefined;
  }

  return validator(value) ? value : undefined;
};

const getOrderListFiltersFromSearchParams = (
  searchParams: URLSearchParams,
): OrderListFilters => ({
  status: getSearchParamFilterValue(searchParams, "status", isOrderStatus),
  fulfillmentStatus: getSearchParamFilterValue(
    searchParams,
    "fulfillmentStatus",
    isOrderFulfillmentStatus,
  ),
  search: searchParams.get("search") ?? "",
  shops: getShopFilterValuesFromSearchParams(searchParams) ?? [],
});

const getOrderListParamsFromSearchParams = (
  searchParams: URLSearchParams,
): OrderListParams => ({
  orders: [],
  page: searchParams.get("page") ?? "1",
  limit: searchParams.get("limit") ?? DefaultRowsPerPage,
  totalPages: searchParams.get("totalPages") ?? "0",
  ...getOrderListFiltersFromSearchParams(searchParams),
});

const getAllowedShopFilterValues = (
  requestedValues: string[] | undefined,
  allowedValues: string[],
) => {
  if (!requestedValues) {
    return allowedValues;
  }

  return requestedValues.filter((value) => allowedValues.includes(value));
};

const getSelectedStoreFilterLabel = ({
  selectedValues,
  options,
  allOptionsSelected,
  placeholderLabel,
  allStoresLabel,
  selectedStoresLabel,
}: {
  selectedValues: string[];
  options: StoreFilterSelectOption[];
  allOptionsSelected: boolean;
  placeholderLabel: string;
  allStoresLabel: string;
  selectedStoresLabel: string;
}) => {
  if (!selectedValues.length) {
    return placeholderLabel;
  }

  if (allOptionsSelected) {
    return allStoresLabel;
  }

  if (selectedValues.length === 1) {
    return (
      options.find((store) => store.value === selectedValues[0])?.label ??
      placeholderLabel
    );
  }

  return selectedStoresLabel;
};

export function Orders() {
  const { t } = useTranslation();

  const { translateFulfillmentStatus, translateOrderStatus } =
    useTranslateStatus();

  const translatedFilterStatusOptions = useMemo(
    () =>
      filterStatusOptions.map((option) => ({
        ...option,
        label: translateOrderStatus(option.value),
      })),
    [translateOrderStatus],
  );

  const translatedFilterFulfillmentStatusOptions = useMemo(
    () =>
      filterFulfillmentStatusOptions.map((option) => ({
        ...option,
        label: translateFulfillmentStatus(option.value),
      })),
    [translateFulfillmentStatus],
  );

  const [ordersSearchParams, setOrdersSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [storeFilterOpen, setStoreFilterOpen] = useState(false);
  const [ordersList, setOrdersList] = useState<OrderListParams>(
    getOrderListParamsFromSearchParams(ordersSearchParams),
  );

  const { orders, limit } = ordersList;

  const { shopSelected, user } = useAuth();

  const dateFormat = shopSelected?.tenant?.dateFormat;

  const storeFilterOptions = useMemo(
    () => getStoreFilterOptions(user?.user?.shopsGranted ?? []),
    [user?.user?.shopsGranted],
  );

  const defaultShopFilterValues = useMemo(
    () => storeFilterOptions.map((store) => store.value),
    [storeFilterOptions],
  );

  const allStoresSelected =
    defaultShopFilterValues.length > 0 &&
    defaultShopFilterValues.every((store) => ordersList.shops.includes(store));

  const storeFilterLabel = getSelectedStoreFilterLabel({
    selectedValues: ordersList.shops,
    options: storeFilterOptions,
    allOptionsSelected: allStoresSelected,
    placeholderLabel: t("selectStore"),
    allStoresLabel: t("allStores"),
    selectedStoresLabel: t("selectedStores", {
      count: ordersList.shops.length,
    }),
  });

  const fetchOrders = useCallback(async (params: OrderListParams) => {
    setLoading(true);

    try {
      const { data } = await wrapperRequests(
        routesURL.report.orders.getOrders,
        "GET",
        {
          params: {
            searchQuery: params.search || undefined,
            fulfillmentStatus: params.fulfillmentStatus
              ? [params.fulfillmentStatus]
              : undefined,
            processStatus: params.status
              ? [params.status]
              : defaultOrderProcessStatusFilters,
            shops: JSON.stringify(getShopFilterParams(params.shops)),
            page: params.page,
            limit: params.limit,
          },
        },
      );

      setOrdersList((state) => ({
        ...state,
        orders: data.orders,
        totalPages: data.totalPages,
      }));
    } catch (error) {
      if (error instanceof AxiosError) {
        throw Error(error.response?.data.message);
      }
      throw Error(String(error));
    } finally {
      setLoading(false);
    }
  }, []);

  const updateFiltersValue = <K extends keyof OrderListFilters>(
    key: K,
    value: OrderListFilters[K],
  ) => {
    setOrdersList((state) => ({
      ...state,
      [key]: value,
    }));
  };

  const toggleShopFilter = (shopValue: string) => {
    setOrdersList((state) => {
      const shopAlreadySelected = state.shops.includes(shopValue);

      return {
        ...state,
        shops: shopAlreadySelected
          ? state.shops.filter((shop) => shop !== shopValue)
          : [...state.shops, shopValue],
      };
    });
  };

  const toggleAllShopFilters = () => {
    updateFiltersValue(
      "shops",
      allStoresSelected ? [] : defaultShopFilterValues,
    );
  };

  const clearStatusFilters = () => {
    setOrdersList((state) => ({
      ...state,
      status: undefined,
      shops: defaultShopFilterValues,
      fulfillmentStatus: undefined,
    }));
  };

  const updateSearchParams = () => {
    const updatedParams = new URLSearchParams();

    ordersList.search && updatedParams.set("search", ordersList.search);
    ordersList.status && updatedParams.set("status", ordersList.status);
    ordersList.fulfillmentStatus &&
      updatedParams.set("fulfillmentStatus", ordersList.fulfillmentStatus);
    updatedParams.set(SHOPS_SEARCH_PARAM, JSON.stringify(ordersList.shops));

    updatedParams.set("page", "1");
    updatedParams.set("limit", String(limit));

    setStoreFilterOpen(false);
    setOrdersSearchParams(updatedParams);
  };

  const resetParams = () => {
    setStoreFilterOpen(false);
    clearStatusFilters();

    setOrdersSearchParams({
      limit: DefaultRowsPerPage,
      page: "1",
      [SHOPS_SEARCH_PARAM]: JSON.stringify(defaultShopFilterValues),
    });
  };

  const updateRows = (value: number) => {
    setOrdersSearchParams((params) => {
      const updatedParams = new URLSearchParams(params.toString());
      updatedParams.set("limit", String(value));
      updatedParams.set("page", "1");
      return updatedParams;
    });
  };

  const updatePage = (value: number) => {
    setOrdersSearchParams((params) => {
      const updatedParams = new URLSearchParams(params.toString());
      updatedParams.set("page", String(value));
      return updatedParams;
    });
  };

  const navigate = useNavigate();

  const columns: ColumnDef<OrderList>[] = [
    {
      accessorKey: "orderReferenceNumber",
      header: t("orderId"),
      size: 140,
      meta: { fitContent: true },
      cell: ({ row }) => (
        <CopyContainer stringValue={row.original.orderReferenceNumber} />
      ),
    },
    {
      accessorKey: "storeOrderID",
      header: t("siteOrderId"),
      size: 140,
      meta: { fitContent: true },
      cell: ({ row }) => (
        <CopyContainer stringValue={row.original.storeOrderID} />
      ),
    },
    {
      accessorKey: "createdAt",
      header: t("orderDate"),
      size: 160,
      meta: { fitContent: true },
      cell: ({ row }) =>
        formatDateWithTenantFormat(row.original.createdAt, dateFormat, true),
    },
    {
      accessorFn: (row) => {
        const { billing, shipping } = row;
        return billing?.firstName || shipping?.firstName;
      },
      header: t("customerName"),
      size: 160,
      cell: ({ row }) => {
        const { billing, shipping } = row.original;
        return billing?.firstName
          ? `${billing.lastName}, ${billing.firstName}`
          : `${shipping?.lastName}, ${shipping?.firstName}`;
      },
    },
    {
      accessorKey: "processMessage",
      header: t("errorMessage"),
      size: 160,
      cell: ({ row }) => (
        <S.ErrorMessage>
          {ConvertStringWithoutValueToDefault(row.original.processMessage)}
        </S.ErrorMessage>
      ),
    },
    {
      accessorFn: (row) => row.processStatus,
      header: t("status"),
      size: 170,
      cell: ({ row }) => {
        const status = row.original.processStatus;
        return (
          <S.StatusTag status={status} title={OrderStatusConverter[status]}>
            {ReduceString(translateOrderStatus(status), 100)}
          </S.StatusTag>
        );
      },
    },
    {
      accessorFn: (row) => row.fulfillmentStatus,
      header: t("fulfillmentStatus"),
      size: 180,
      enableSorting: false,
      meta: { fitContent: true },
      cell: ({ row }) => {
        const fulfillmentStatus = row.original.fulfillmentStatus;

        if (!fulfillmentStatus) {
          return ConvertStringWithoutValueToDefault(fulfillmentStatus);
        }

        return (
          <S.StatusTag
            status={fulfillmentStatus}
            title={translateFulfillmentStatus(fulfillmentStatus)}
          >
            {ReduceString(translateFulfillmentStatus(fulfillmentStatus), 100)}
          </S.StatusTag>
        );
      },
    },
    {
      accessorKey: "",
      header: t("actions"),
      size: 160,
      cell: ({ row }) => (
        <MdOutlineModeEditOutline
          size={24}
          style={{ cursor: "pointer" }}
          title={t("edit")}
          onClick={() => navigate(`/report/orders/${row.original.id}`)}
        />
      ),
    },
  ];

  useEffect(() => {
    if (!defaultShopFilterValues.length) {
      return;
    }

    const next = getOrderListParamsFromSearchParams(ordersSearchParams);
    const searchParamShopValues =
      getShopFilterValuesFromSearchParams(ordersSearchParams);
    const nextShopValues = getAllowedShopFilterValues(
      searchParamShopValues,
      defaultShopFilterValues,
    );
    const nextOrdersListParams = {
      ...next,
      shops: nextShopValues,
    };

    setOrdersList((prev) => ({ ...prev, ...nextOrdersListParams }));
    fetchOrders(nextOrdersListParams);
  }, [ordersSearchParams, defaultShopFilterValues, fetchOrders]);

  return (
    <S.Container>
      <PageHeader title={t("orders")} description={t("seeAllOrders")} />
      <S.Content>
        <SearchInput
          disabled={loading}
          title={t("searchByIdentifier")}
          placeholder={t("searchByOrderPlaceholder")}
          value={ordersList.search}
          onChange={(event) => updateFiltersValue("search", event.target.value)}
          onPressEnter={updateSearchParams}
          filterDropdown={{
            disabled: loading,
            selectInput: (
              <>
                <SelectInput
                  title={t("status")}
                  placeholder={t("selectStatus")}
                  value={ordersList.status ?? ""}
                  variant="resized"
                  options={translatedFilterStatusOptions}
                  onChange={(event) => {
                    updateFiltersValue(
                      "status",
                      (event.target.value as OrderStatus) || undefined,
                    );
                  }}
                />
                <StoreFilterSelect
                  title={t("store")}
                  valueLabel={storeFilterLabel}
                  selectAllLabel={t("selectAll")}
                  options={storeFilterOptions}
                  selectedValues={ordersList.shops}
                  allOptionsSelected={allStoresSelected}
                  isOpen={storeFilterOpen}
                  onToggleOpen={() => setStoreFilterOpen((state) => !state)}
                  onToggleAll={toggleAllShopFilters}
                  onToggleOption={toggleShopFilter}
                />
                <SelectInput
                  title={t("fulfillmentStatus")}
                  placeholder={t("selectFulfillmentStatus")}
                  value={ordersList.fulfillmentStatus ?? ""}
                  variant="resized"
                  options={translatedFilterFulfillmentStatusOptions}
                  onChange={(event) => {
                    updateFiltersValue(
                      "fulfillmentStatus",
                      (event.target.value as OrderFulfillmentStatus) ||
                        undefined,
                    );
                  }}
                />
              </>
            ),
            onFilterClick: updateSearchParams,
            onResetClick: resetParams,
          }}
        />
        {loading && <Loading />}

        {orders.length === 0 && !loading && <NoDataAvailableImage />}

        {orders.length > 0 && !loading && (
          <>
            <Table
              data={orders}
              columns={columns}
              columnToFilter=""
              key={orders.length}
              fitContentColumns={[
                "orderReferenceNumber",
                "storeOrderID",
                "createdAt",
              ]}
              style={{
                marginBottom: "2rem",
                width: "100%",
              }}
              pagination={{
                rowsPerPage: Number(limit),
                selectPage: updatePage,
                selectRowsPerPage: updateRows,
                page: Number(ordersList.page),
                totalPages: Number(ordersList.totalPages),
              }}
            />
          </>
        )}
      </S.Content>
    </S.Container>
  );
}
